/**
 * KpiTile — W46 Dashboard KPI tile component.
 * No Recharts. Pure React/Tailwind.
 * Visual canon: 5-color palette, teal accents, no exclamation points, no emojis.
 */

import React from "react";

interface KpiTileProps {
  label: string;
  value: string | number | null;
  subValue?: string;
  trend?: "up" | "down" | "flat" | null;
  trendLabel?: string;
  colorVariant?: "default" | "green" | "red" | "orange" | "yellow" | "teal";
  loading?: boolean;
  /** D-098 caveat: when true, render "Not yet scored" state */
  unscored?: boolean;
}

const VARIANT_COLORS: Record<string, string> = {
  default: "text-foreground",
  green:   "text-[#16A34A]",
  red:     "text-[#DC2626]",
  orange:  "text-[#EA580C]",
  yellow:  "text-[#EAB308]",
  teal:    "text-[#0F766E]",
};

const TREND_ICONS = {
  up:   (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 10 L7 4 L12 10" />
    </svg>
  ),
  down: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 4 L7 10 L12 4" />
    </svg>
  ),
  flat: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 7 L12 7" />
    </svg>
  ),
};

export function KpiTile({
  label,
  value,
  subValue,
  trend,
  trendLabel,
  colorVariant = "default",
  loading = false,
  unscored = false,
}: KpiTileProps) {
  const valueColor = VARIANT_COLORS[colorVariant] || VARIANT_COLORS.default;
  const trendColor =
    trend === "up"   ? "text-[#16A34A]"
    : trend === "down" ? "text-[#DC2626]"
    : "text-[#6B7280]";

  return (
    <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4 flex flex-col gap-1 min-w-0">
      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide truncate">
        {label}
      </p>
      {loading ? (
        <div className="h-7 w-3/4 bg-[hsl(var(--surface-2))] rounded animate-pulse" />
      ) : unscored ? (
        <p className="text-lg font-semibold text-[#6B7280]">Not yet scored</p>
      ) : (
        <p className={`text-2xl font-bold tabular-nums leading-tight ${valueColor}`}>
          {value ?? "\u2014"}
        </p>
      )}
      {subValue && !loading && !unscored && (
        <p className="text-[12px] text-muted-foreground truncate">{subValue}</p>
      )}
      {trend && trendLabel && !loading && !unscored && (
        <div className={`flex items-center gap-1 text-[12px] ${trendColor}`}>
          {TREND_ICONS[trend]}
          <span>{trendLabel}</span>
        </div>
      )}
    </div>
  );
}
